import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import map from "lodash/map";
import difference from "lodash/difference";

import ModalGeneric from "../../../Common/ModalGeneric";
import UpdateOpenModal from "../../CreateModals/UpdateOpenModal";
import BybetjentCreateModalContent from "../../BybetjentForm";

import {
  putBybetjenter,
  deleteBetjentsTeam,
  addBetjentToTeam,
  getBybetjenter,
} from "../../../../actions/bybetjenterActions";
import {
  IsValidated,
  onChangeValidation,
} from "../../../../utils/validationUtils";
import { toIsoString } from "../../../../utils/date";

const rules = {
  navn: "required;minLength=3",
  nummer: "required;isDigit",
  mobil: "isDigit;length=8",
  aktivertDato: "isDato;isLessThan=deaktivertDato",
  deaktivertDato: "isDato;isLargerThan=aktivertDato",
  epost: "isEmail",
  rolle: "required",
  seksjon: "required",
};

function stateFromProps(p) {
  return {
    open: false,
    submitButtonText: "Lagre",
    betjent: {},
    nummer: p.nummer,
    currentTeam: p.team,
    id: p.id,
    modifiedTeam: p.team,
    navn: p.navn,
    mobil: p.mobil,
    rolle: p.rolle,
    seksjon: p.seksjon,
    epost: p.epost,
    aktivertDato: p.aktivertDato,
    deaktivertDato: p.deaktivertDato,
    validationError: {},
    openDeaktiverConfirm: false,
  };
}

function Bybetjent({
  id,
  nummer,
  team,
  navn,
  mobil,
  rolle,
  seksjon,
  epost,
  aktivertDato,
  deaktivertDato,
}) {
  const dispatch = useDispatch();
  const teamList = useSelector((state) => state.betjentgrupper);
  const rollerRaw = useSelector((state) => state.betjentroller);
  const seksjoner = useSelector((state) => state.bybetjenterseksjoner);

  const props = {
    id,
    nummer,
    team,
    navn,
    mobil,
    rolle,
    seksjon,
    epost,
    aktivertDato,
    deaktivertDato,
  };
  const [state, setState] = useState(() => stateFromProps(props));
  const update = (patch) => setState((s) => ({ ...s, ...patch }));

  useEffect(() => {
    setState((s) => ({ ...stateFromProps(props), open: s.open }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    nummer,
    navn,
    mobil,
    rolle,
    seksjon,
    epost,
    aktivertDato,
    deaktivertDato,
  ]);

  const closeModal = () => setState(stateFromProps(props));
  const openModal = () => update({ open: true });

  const isValid = () => {
    const { isValid, validationError } = IsValidated(state, rules);
    update({ validationError });
    return isValid;
  };

  const roller = map(rollerRaw, (i) =>
    Object.assign([], { id: i.kode, navn: i.beskrivelse }),
  );

  const generateTeamPayload = (s) => {
    const currentTeamIds = s.currentTeam.map((i) => i.id);
    const modifiedTeamIds = s.modifiedTeam.map((i) => i.id);
    return {
      id: s.id,
      nummer: s.nummer,
      navn: s.navn,
      teamIdsToDelete: difference(currentTeamIds, modifiedTeamIds),
      teamIdsToAdd: difference(modifiedTeamIds, currentTeamIds),
      seksjon: s.seksjon,
      rolle: s.rolle,
      mobil: s.mobil,
      epost: s.epost,
      aktivertDato: s.aktivertDato ? toIsoString(s.aktivertDato) : null,
      deaktivertDato: s.deaktivertDato ? toIsoString(s.deaktivertDato) : null,
    };
  };

  const updateBetjentTeam = async (payload) => {
    const { id: betjentId, teamIdsToAdd, teamIdsToDelete } = payload;
    if (betjentId !== "" && teamIdsToAdd.length > 0) {
      await Promise.all(
        map(teamIdsToAdd, (item) =>
          dispatch(addBetjentToTeam(betjentId, item)),
        ),
      );
    }
    if (betjentId !== "" && teamIdsToDelete.length > 0) {
      await Promise.all(
        map(teamIdsToDelete, (item) =>
          dispatch(deleteBetjentsTeam(betjentId, item)),
        ),
      );
    }
  };

  const handlePut = async () => {
    const payload = generateTeamPayload(state);
    await dispatch(putBybetjenter(payload));
    await updateBetjentTeam(payload);
    await dispatch(getBybetjenter());
    closeModal();
  };

  const saveAndClose = async () => {
    if (isValid() && !state.deaktivertDato) {
      await handlePut();
    } else if (isValid() && state.deaktivertDato) {
      update({ open: false, openDeaktiverConfirm: true });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const parsedValue = name === "nummer" ? parseInt(value) : value;
    const patch =
      name === "team" ? { modifiedTeam: value } : { [name]: parsedValue };
    const draft = { ...state, ...patch };
    onChangeValidation(e, draft, rules);
    setState(draft);
  };

  return (
    <React.Fragment>
      <ModalGeneric
        modalTitle="Rediger bypatrulje ansatt"
        open={state.open}
        closeModal={closeModal}
        openModal={openModal}
        OpenModalComponent={UpdateOpenModal}
        submit={saveAndClose}
        submitButtonText={state.submitButtonText}
      >
        <BybetjentCreateModalContent
          id={id}
          nummer={state.nummer}
          teamList={teamList}
          team={state.modifiedTeam}
          navn={state.navn}
          mobil={state.mobil}
          epost={state.epost}
          roller={roller}
          rolle={state.rolle}
          seksjon={state.seksjon}
          seksjoner={seksjoner}
          aktivertDato={state.aktivertDato}
          deaktivertDato={state.deaktivertDato}
          validationError={state.validationError}
          handleChange={handleChange}
          actionType="update"
        />
      </ModalGeneric>
      <ModalGeneric
        modalTitle="Deaktiver bruker"
        open={state.openDeaktiverConfirm}
        closeModal={() => update({ openDeaktiverConfirm: false, open: true })}
        submit={handlePut}
        submitButtonText={state.submitButtonText}
      >
        <p>Vil du deaktivere betjent {state.navn}?</p>
      </ModalGeneric>
    </React.Fragment>
  );
}

export default Bybetjent;
